import { Icon, type IconName } from './icon';
import styles from './dietary-indicators.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

// The indicator family is vegan · vegetarian · gluten-free · dairy-free. Nut-free
// was dropped (#481): there is no recipe-level nut-free signal, so it is neither
// an indicator nor a filter.
export type DietKey = 'vegan' | 'vegetarian' | 'glutenFree' | 'dairyFree';

/** Canonical dietary glyphs — vegan (sprout) and vegetarian (leaf) are distinct. */
export const DIET_ICON: Record<DietKey, IconName> = {
  vegan: 'sprout',
  vegetarian: 'leaf',
  glutenFree: 'wheat-off',
  dairyFree: 'milk-off',
};

/** One indicator: its key (→ icon) + a pre-translated label (tooltip / a11y name). */
export type DietaryItem = { key: DietKey; label: string };

type Density = 'card' | 'hero';

interface DietaryIndicatorsProps {
  items: DietaryItem[];
  /** 'card' = tight icon-only w/ tooltip; 'hero' = icon + label. */
  density?: Density;
  /** Overflow into a +N chip past this many (card density). */
  max?: number;
  /** Accessible name for the group (e.g. "Dietary information"). */
  label?: string;
  className?: string;
}

/**
 * Quiet, muted dietary indicators — a calm at-a-glance signal, not a billboard.
 * Icon-only on cards (tooltip + aria-label carry the meaning), icon + text on
 * the roomier hero. Presentational: labels are passed in already translated.
 */
export function DietaryIndicators({
  items,
  density = 'card',
  max = 4,
  label,
  className,
}: DietaryIndicatorsProps) {
  if (items.length === 0) return null;
  const shown = density === 'card' ? items.slice(0, max) : items;
  const hidden = density === 'card' ? items.slice(max) : [];

  return (
    <div
      className={cx(styles.row, density === 'hero' ? styles.rowHero : styles.rowCard, className)}
      role="group"
      aria-label={label}
    >
      {shown.map((it) =>
        density === 'hero' ? (
          <span key={it.key} className={cx(styles.di, styles.diHero)} data-diet={it.key} role="img" aria-label={it.label}>
            <Icon name={DIET_ICON[it.key]} size={16} strokeWidth={1.75} aria-hidden="true" />
            <span className={styles.diText}>{it.label}</span>
          </span>
        ) : (
          <span
            key={it.key}
            className={cx(styles.di, styles.diCard, styles.tip)}
            data-diet={it.key}
            role="img"
            aria-label={it.label}
            data-tip={it.label}
            tabIndex={0}
          >
            <Icon name={DIET_ICON[it.key]} size={14} strokeWidth={1.75} aria-hidden="true" />
          </span>
        ),
      )}
      {hidden.length > 0 && (
        <span
          className={cx(styles.di, styles.diCard, styles.diMore, styles.tip)}
          role="img"
          aria-label={hidden.map((h) => h.label).join(', ')}
          data-tip={hidden.map((h) => h.label).join(' · ')}
          tabIndex={0}
        >
          {`+${hidden.length}`}
        </span>
      )}
    </div>
  );
}

const HASHI_FRAC: Record<'high' | 'medium' | 'low', number> = {
  high: 1,
  medium: 0.666,
  low: 0.333,
};

interface HashiTierProps {
  tier: 'high' | 'medium' | 'low';
  /** Full accessible name / tooltip (e.g. "Hashimoto-friendly · 82%"). */
  srLabel: string;
  /** Visible "Hashimoto" label when showLabel. */
  label?: string;
  /** Short tier word (High / Med / Low) shown bold when showLabel. */
  shortLabel?: string;
  showLabel?: boolean;
}

/**
 * Hashimoto suitability as a fractional-arc ring (high/medium/low) — a tier,
 * not a boolean. Icon-only by default with the score in the tooltip.
 */
export function HashiTier({ tier, srLabel, label, shortLabel, showLabel = false }: HashiTierProps) {
  const R = 7;
  const C = 2 * Math.PI * R;
  const frac = HASHI_FRAC[tier];

  return (
    <span
      className={cx(styles.hashi, showLabel && styles.hashiLabelled, styles.tip)}
      role="img"
      aria-label={srLabel}
      data-tip={srLabel}
      tabIndex={0}
    >
      <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true" className={styles.hashiRing}>
        <circle cx="10" cy="10" r={R} className={styles.hashiTrack} />
        <circle
          cx="10"
          cy="10"
          r={R}
          className={styles.hashiArc}
          strokeDasharray={`${C * frac} ${C}`}
          transform="rotate(-90 10 10)"
        />
        <circle cx="10" cy="10" r="1.6" className={styles.hashiCore} />
      </svg>
      {showLabel && label ? (
        <span className={styles.hashiText}>
          {label}
          {shortLabel ? (
            <>
              {' '}
              <b>{shortLabel}</b>
            </>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
